import React, { useRef, useCallback, useState } from 'react';
import Editor from '@monaco-editor/react';
import { Button, Icon, Modal } from 'semantic-ui-react';
import { showSuccess, showError } from '../helpers';

/**
 * Monaco 代码编辑器组件
 * 
 * 支持 JavaScript, Go Template, JSON 等语言的语法高亮和智能提示
 * 支持复制和全屏功能
 */
const MonacoCodeEditor = ({
  value,
  onChange,
  onValidate, // 语法验证回调: ({ errors, warnings }) => void
  language = 'javascript',
  height = '400px',
  readOnly = false,
  theme = 'vs-dark',
  placeholder = '',
  options = {},
  showToolbar = true, // 是否显示工具栏（复制、全屏按钮）
  title = '代码编辑器', // 全屏模式的标题
}) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);

  // 编辑器挂载完成
  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // 监听 markers 变化（语法错误）
    if (onValidate) {
      const model = editor.getModel();
      if (model) {
        // 延迟获取初始 markers（等待语法检查完成）
        setTimeout(() => {
          const markers = monaco.editor.getModelMarkers({ resource: model.uri });
          const errors = markers.filter(m => m.severity === monaco.MarkerSeverity.Error);
          const warnings = markers.filter(m => m.severity === monaco.MarkerSeverity.Warning);
          onValidate({ errors, warnings });
        }, 500);

        // 监听 markers 变化
        const disposable = monaco.editor.onDidChangeMarkers((uris) => {
          if (uris.some(uri => uri.toString() === model.uri.toString())) {
            const markers = monaco.editor.getModelMarkers({ resource: model.uri });
            const errors = markers.filter(m => m.severity === monaco.MarkerSeverity.Error);
            const warnings = markers.filter(m => m.severity === monaco.MarkerSeverity.Warning);
            onValidate({ errors, warnings });
          }
        });
        
        // 返回清理函数（虽然这里不会执行，因为在 useCallback 里）
        editor.onDidDispose(() => disposable.dispose());
      }
    }

    // 为 Go Template 语言注册语法高亮（如果尚未注册）
    if (!monaco.languages.getLanguages().some(lang => lang.id === 'gotemplate')) {
      // 注册 Go Template 语言
      monaco.languages.register({ id: 'gotemplate' });
      
      // 设置 tokenizer
      monaco.languages.setMonarchTokensProvider('gotemplate', {
        tokenizer: {
          root: [
            // Go Template 语法
            [/\{\{-?\s*/, 'delimiter.bracket', '@gotemplate'],
            // 普通文本
            [/[^{]+/, 'text'],
          ],
          gotemplate: [
            // 注释
            [/\/\*.*?\*\//, 'comment'],
            // 关键字
            [/\b(if|else|end|range|with|define|template|block|nil|true|false)\b/, 'keyword'],
            // 内置函数
            [/\b(and|call|html|index|slice|js|len|not|or|print|printf|println|urlquery|eq|ne|lt|le|gt|ge|upper|lower|trim|replace|escape|isEmpty|notEmpty|truncate|join|split|default|toJSON)\b/, 'support.function'],
            // 变量
            [/\$[a-zA-Z_][a-zA-Z0-9_]*/, 'variable'],
            [/\.[a-zA-Z_][a-zA-Z0-9_]*/, 'variable.property'],
            // 字符串
            [/"[^"]*"/, 'string'],
            [/`[^`]*`/, 'string'],
            // 数字
            [/\d+(\.\d+)?/, 'number'],
            // 管道符
            [/\|/, 'delimiter'],
            // 结束
            [/-?\s*\}\}/, 'delimiter.bracket', '@pop'],
            // 空白
            [/\s+/, 'white'],
          ],
        },
      });

      // 设置编辑器配置
      monaco.languages.setLanguageConfiguration('gotemplate', {
        brackets: [
          ['{{', '}}'],
          ['(', ')'],
        ],
        autoClosingPairs: [
          { open: '{{', close: '}}' },
          { open: '"', close: '"' },
          { open: '`', close: '`' },
          { open: '(', close: ')' },
        ],
        surroundingPairs: [
          { open: '{{', close: '}}' },
          { open: '"', close: '"' },
          { open: '`', close: '`' },
        ],
      });

      // 注册自动补全
      monaco.languages.registerCompletionItemProvider('gotemplate', {
        provideCompletionItems: (model, position) => {
          const suggestions = [
            // 控制结构
            { label: 'if', kind: monaco.languages.CompletionItemKind.Keyword, insertText: '{{ if ${1:condition} }}\n\t$0\n{{ end }}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
            { label: 'if-else', kind: monaco.languages.CompletionItemKind.Keyword, insertText: '{{ if ${1:condition} }}\n\t${2:then}\n{{ else }}\n\t${3:else}\n{{ end }}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
            { label: 'range', kind: monaco.languages.CompletionItemKind.Keyword, insertText: '{{ range ${1:\\$item} := ${2:.items} }}\n\t$0\n{{ end }}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
            { label: 'with', kind: monaco.languages.CompletionItemKind.Keyword, insertText: '{{ with ${1:.value} }}\n\t$0\n{{ end }}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
            // 内置函数
            { label: 'upper', kind: monaco.languages.CompletionItemKind.Function, insertText: 'upper ${1:string}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: '转为大写' },
            { label: 'lower', kind: monaco.languages.CompletionItemKind.Function, insertText: 'lower ${1:string}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: '转为小写' },
            { label: 'trim', kind: monaco.languages.CompletionItemKind.Function, insertText: 'trim ${1:string}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: '去除首尾空白' },
            { label: 'replace', kind: monaco.languages.CompletionItemKind.Function, insertText: 'replace ${1:old} ${2:new} ${3:string}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: '替换字符串' },
            { label: 'default', kind: monaco.languages.CompletionItemKind.Function, insertText: 'default ${1:defaultValue} ${2:.value}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: '默认值' },
            { label: 'toJSON', kind: monaco.languages.CompletionItemKind.Function, insertText: 'toJSON ${1:.value}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: '转为 JSON' },
            { label: 'truncate', kind: monaco.languages.CompletionItemKind.Function, insertText: 'truncate ${1:.text} ${2:100}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: '截断文本' },
          ];
          return { suggestions };
        },
      });
    }
  }, [onValidate]);

  // 值变化
  const handleChange = useCallback((newValue) => {
    if (onChange) {
      onChange(newValue || '');
    }
  }, [onChange]);

  // 编辑器加载前配置
  const handleBeforeMount = useCallback((monaco) => {
    // 自定义主题
    monaco.editor.defineTheme('one-api-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'delimiter.bracket', foreground: '569cd6' },
        { token: 'keyword', foreground: 'c586c0' },
        { token: 'support.function', foreground: 'dcdcaa' },
        { token: 'variable', foreground: '9cdcfe' },
        { token: 'variable.property', foreground: '4ec9b0' },
      ],
      colors: {
        'editor.background': '#1e1e1e',
        'editor.foreground': '#d4d4d4',
        'editorLineNumber.foreground': '#858585',
        'editorLineNumber.activeForeground': '#c6c6c6',
        'editor.selectionBackground': '#264f78',
        'editor.lineHighlightBackground': '#2f2f2f',
      },
    });
  }, []);

  // 复制到剪贴板
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(true);
      showSuccess('已复制到剪贴板');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showError('复制失败: ' + err.message);
    }
  };

  // 合并默认选项和传入选项
  const editorOptions = {
    minimap: { enabled: false },
    fontSize: 13,
    fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
    lineHeight: 20,
    tabSize: 2,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    wordWrap: 'on',
    readOnly,
    padding: { top: showToolbar ? 50 : 12, bottom: 12 }, // 如果有工具栏，增加顶部padding
    scrollbar: {
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10,
    },
    ...options,
  };

  // 渲染编辑器
  const renderEditor = (isFullscreen = false) => (
    <div 
      className="monaco-editor-wrapper" 
      style={{ 
        height: isFullscreen ? '70vh' : height, 
        border: '1px solid #3c3c3c', 
        borderRadius: '6px', 
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {showToolbar && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 100,
          display: 'flex',
          gap: '6px'
        }}>
          <Button
            size="mini"
            icon
            basic
            inverted
            title="复制代码"
            onClick={handleCopy}
            style={{ opacity: 0.8 }}
          >
            <Icon name={copied ? 'check' : 'copy'} />
          </Button>
          {!isFullscreen && (
            <Button
              size="mini"
              icon
              basic
              inverted
              title="全屏编辑"
              onClick={() => setFullscreen(true)}
              style={{ opacity: 0.8 }}
            >
              <Icon name="expand" />
            </Button>
          )}
        </div>
      )}
      <Editor
        height="100%"
        language={language}
        value={value}
        theme={theme === 'vs-dark' ? 'one-api-dark' : theme}
        onChange={onChange}
        onMount={handleEditorDidMount}
        beforeMount={handleBeforeMount}
        options={editorOptions}
        loading={<div style={{ padding: '20px', color: '#888' }}>Loading editor...</div>}
      />
    </div>
  );

  return (
    <>
      {renderEditor(false)}
      
      {/* 全屏模态框 */}
      <Modal
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        size="fullscreen"
        style={{ height: '90vh' }}
      >
        <Modal.Header>
          {title}
          <Button
            floated="right"
            icon
            basic
            onClick={() => setFullscreen(false)}
            style={{ marginTop: '-5px' }}
          >
            <Icon name="close" />
          </Button>
        </Modal.Header>
        <Modal.Content style={{ padding: '20px', height: 'calc(90vh - 120px)' }}>
          {renderEditor(true)}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCopy} primary>
            <Icon name="copy" /> 复制
          </Button>
          <Button onClick={() => setFullscreen(false)}>
            关闭
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default MonacoCodeEditor;
